import { SeoLandingLayout, SeoLandingPageData } from '../../../components/SeoLandingLayout';

const data: SeoLandingPageData = {
  seo: {
    title: 'Problème de cash flow : causes et solutions pour votre entreprise | Trezoone',
    description: 'Vous avez un problème de cash flow ? Identifiez les causes et trouvez les solutions adaptées. Guide pratique pour redresser la situation.',
    canonical: '/probleme-cash-flow',
    keywords: 'problème cash flow, problème flux trésorerie, cash flow négatif entreprise, résoudre problème trésorerie',
  },
  hero: {
    tag: 'Problème cash flow',
    title: 'Problème de cash flow : 9 fois sur 10, c\'est évitable. Souvent prévisible.',
    subtitle: 'Un problème de cash flow n\'arrive pas par magie. Il a une cause précise — et une solution. Voici comment diagnostiquer le vôtre.',
    ctaLabel: 'Diagnostiquer ma situation',
    ctaHref: '/simulateur-tresorerie-association',
  },
  pilierLink: { label: 'Gestion de trésorerie', href: '/gestion-tresorerie' },
  sections: [
    {
      type: 'explain',
      title: 'Les 7 causes les plus fréquentes d\'un problème de cash flow',
      content: 'Un problème de cash flow a presque toujours une origine identifiable :',
      items: [
        '1. Délais de paiement clients trop longs → vous attendez 60-90 jours',
        '2. Croissance rapide non financée → votre BFR augmente plus vite que votre trésorerie',
        '3. Marges insuffisantes → vous travaillez beaucoup pour peu de cash',
        '4. Dépenses non planifiées → une réparation, un litige, une pénalité',
        '5. Pertes sur créances → un client important ne paie pas',
        '6. Saisonnalité non provisionnée → les basses saisons vident la réserve',
        '7. Gestion à vue → sans plan de trésorerie, vous ne voyez rien venir',
      ],
    },
    {
      type: 'example',
      title: 'Diagnostic rapide : quelle est votre cause ?',
      content: 'Posez-vous ces 3 questions pour identifier rapidement la cause :',
      items: [
        'Q1 : Avez-vous des créances clients impayées en retard ? → Cause n°1 ou 5',
        'Q2 : Votre activité a-t-elle fortement progressé ces derniers mois ? → Cause n°2',
        'Q3 : Avez-vous eu des dépenses exceptionnelles non prévues ? → Cause n°4',
      ],
    },
    {
      type: 'midcta',
      title: 'Diagnostiquez votre problème de cash flow en 3 questions — et recevez un plan d\'action',
      content: 'Trezoone analyse vos flux, identifie la cause de vos tensions et vous donne les leviers pour retrouver un cash flow sain.',
    },
    {
      type: 'solution',
      title: 'Résoudre et prévenir avec Trezoone',
      content: 'Trezoone vous aide à identifier la cause, mesurer l\'impact et prévenir la récurrence.',
      items: [
        'Analyse automatique des flux pour identifier les causes de tension',
        'Simulation de l\'impact de chaque cause sur votre trésorerie future',
        'Plan de trésorerie pour anticiper les prochaines tensions',
        'Alertes précoces avant d\'atteindre les seuils critiques',
        'Rapport pour votre banque ou vos investisseurs',
      ],
    },
    {
      type: 'cluster',
      title: 'Ressources pour résoudre vos problèmes de trésorerie',
      links: [
        { label: 'Manque de trésorerie', href: '/manque-tresorerie' },
        { label: 'Cash flow entreprise', href: '/cash-flow-entreprise' },
        { label: 'BFR et trésorerie', href: '/bfr-tresorerie' },
        { label: 'Stress test trésorerie', href: '/stress-test-tresorerie' },
        { label: 'Alerte trésorerie', href: '/alerte-tresorerie' },
        { label: 'Prévision trésorerie', href: '/prevision-tresorerie' },
        { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
        { label: 'Simulateur trésorerie', href: '/simulateur-tresorerie-association' },
      ],
    },
    {
      type: 'cta',
      title: 'Diagnostiquez et résolvez votre problème de cash flow',
      content: 'Trezoone identifie vos tensions de trésorerie en quelques minutes. Essai gratuit 14 jours.',
    },
  ],
  relatedLinks: [
    { label: 'Manque de trésorerie', href: '/manque-tresorerie' },
    { label: 'Cash flow entreprise', href: '/cash-flow-entreprise' },
    { label: 'BFR et trésorerie', href: '/bfr-tresorerie' },
    { label: 'Stress test trésorerie', href: '/stress-test-tresorerie' },
    { label: 'Alerte trésorerie', href: '/alerte-tresorerie' },
    { label: 'Prévision trésorerie', href: '/prevision-tresorerie' },
    { label: 'Suivi trésorerie entreprise', href: '/suivi-tresorerie-entreprise' },
    { label: 'Gestion trésorerie', href: '/gestion-tresorerie' },
  ],
};

interface Props { onNavigate?: (p: string) => void; onSignIn?: () => void; onSignUp?: () => void; }
export default function ProblemeCashFlowPage({ onNavigate, onSignIn, onSignUp }: Props) {
  return <SeoLandingLayout data={data} onNavigate={onNavigate} onSignIn={onSignIn} onSignUp={onSignUp} />;
}
